"use client";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { Card, CircularProgress, Divider, InputAdornment, TextField } from "@mui/material";
import React, { useCallback, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";

import Suspense from "@/components/molecules/Suspense";
import useAuth from "@/contexts/auth-context";
import { useNotification } from "@/contexts/notification-context";
import useToast from "@/contexts/toast-context";
import useConversationFilterCounts from "@/hooks/requests/message/use-conversation-filter-counts";
import useStartConversation from "@/hooks/requests/conversations/use-start-conversation";
import { IChat } from "@/interfaces/chat.interfaces";
import IContact from "@/interfaces/contact.interface";
import { ChatContactListMessages } from "@/messages/chat/contact-list.messages";

import { buildNewChat } from "../../helpers";
import ChatItem from "../ChatItem";
import ContactsModal from "../ContactsModal";
import ConversationFilters from "../ConversationFilters";
import ConversationTabs from "../ConversationTabs";
import { NoChatsAvailable } from "../NoChatsAvailable";

// Constants
const LOADING_SPINNER_SIZE = 30;

type Props = {
  messages: ChatContactListMessages;
  isLoading: boolean;
  chats?: IChat[] | null;
  search: string;
  onSearch: (search: string) => void;
  setIsInitialLoad: (state: boolean) => void;
  isChatsEmpty: boolean;
};

function ContactList({
  messages,
  isLoading,
  search,
  onSearch,
  chats,
  setIsInitialLoad,
  isChatsEmpty,
}: Props) {
  const [showContactsModal, setShowContactsModal] = useState(false);
  const {
    onStartNewChat,
    selectedChat,
    activeChatPlatformId,
    conversationView,
    setConversationView,
    conversationFilter,
    setConversationFilter,
    loadMoreChats,
    hasMoreChats,
    isLoadingMoreChats,
    totalChatsCount,
  } = useNotification();

  const platformId = activeChatPlatformId;
  const { user } = useAuth();
  const { showToast } = useToast();
  const { startConversation, isLoading: isLoadingStartConversation } = useStartConversation();
  const filterCounts = useConversationFilterCounts({ platformId, view: conversationView });

  const closeContactsModal = useCallback(() => {
    setShowContactsModal(false);
  }, []);

  const handleConversationSuccess = useCallback(
    (contact: IContact) => {
      const newChat: IChat = buildNewChat(contact, platformId);
      onStartNewChat(newChat);
      closeContactsModal();
    },
    [platformId, onStartNewChat, closeContactsModal]
  );

  const validateUserAndPlatform = useCallback(() => {
    return !!user && typeof platformId === "number";
  }, [user, platformId]);

  const handleStartNewChat = useCallback(
    async (contact: IContact) => {
      if (!validateUserAndPlatform()) return;

      if (contact.active === false) {
        showToast({ text: messages.startConversationInactive, type: "error" });
        return;
      }

      try {
        await startConversation({
          formData: {
            contactId: contact.id,
            platformId: platformId!,
          },
          successMessage: messages.startConversationSuccess,
          errorMessage: messages.startConversationError,
          onSuccess: () => handleConversationSuccess(contact),
          onError: closeContactsModal,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error starting conversation:", error);
        closeContactsModal();
      }
    },
    [
      validateUserAndPlatform,
      startConversation,
      platformId,
      messages.startConversationSuccess,
      messages.startConversationError,
      messages.startConversationInactive,
      handleConversationSuccess,
      closeContactsModal,
      showToast,
    ]
  );

  // A aba/filtro já vem aplicada pela API; aqui só resta o filtro de busca por nome.
  const filteredChats = React.useMemo(() => {
    if (!chats) {
      return [];
    }

    if (!search.trim()) {
      return chats;
    }

    const searchTerm = search.toLowerCase().trim();
    return chats.filter((chat) => (chat.contactName?.toLowerCase() || "").includes(searchTerm));
  }, [chats, search]);

  const shouldLoadChats = !!filteredChats && filteredChats.length > 0;
  const isFilteredChatsEmpty = !filteredChats || filteredChats.length <= 0;

  // Debug information
  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <MDBox p={2} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {showContactsModal && (
          <ContactsModal
            onClose={closeContactsModal}
            onSelect={handleStartNewChat}
            messages={messages}
          />
        )}

        <MDBox pb={1.5} display="flex" alignItems="center" gap={1}>
          <TextField
            placeholder={messages.searchChatContactPlaceholder}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            fullWidth
            size="small"
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
          <MDButton
            variant="contained"
            color="primary"
            iconOnly
            onClick={() => setShowContactsModal(true)}
            disabled={isLoadingStartConversation}
            title={messages.addNewConversation}
            sx={{ flexShrink: 0, borderRadius: "0.75rem" }}
          >
            <AddIcon />
          </MDButton>
        </MDBox>

        <MDBox pb={1.5}>
          <ConversationTabs
            view={conversationView}
            onViewChange={setConversationView}
            messages={messages}
          />
        </MDBox>

        <MDBox pb={1.5}>
          <ConversationFilters
            view={conversationView}
            filter={conversationFilter}
            onFilterChange={setConversationFilter}
            counts={filterCounts}
            messages={messages}
          />
        </MDBox>

        <MDBox sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
          <Suspense
            isSuspended={isFilteredChatsEmpty}
            fallback={
              <NoChatsAvailable
                message={search.trim() ? messages.noChatsFound : messages.noChatsMessage}
              />
            }
          >
            <Suspense
              isSuspended={isLoading}
              fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}
            >
              {shouldLoadChats &&
                filteredChats.map((chat, index) => {
                  const isSelected =
                    selectedChat &&
                    (chat.conversationId && selectedChat.conversationId
                      ? chat.conversationId === selectedChat.conversationId
                      : chat.contactExternalCode === selectedChat.contactExternalCode);

                  return (
                    <React.Fragment key={`chat-fragment-${chat.contactExternalCode}-${index}`}>
                      <ChatItem
                        chat={chat}
                        key={`${chat.contactExternalCode}-${index}`}
                        isSelected={!!isSelected}
                        setIsInitialLoad={setIsInitialLoad}
                        messages={messages}
                      />
                      <Divider sx={{ margin: 0 }} />
                    </React.Fragment>
                  );
                })}

              {hasMoreChats && (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={0.5}
                  py={2}
                >
                  {isLoadingMoreChats ? (
                    <MDBox display="flex" alignItems="center" gap={1} py={0.5}>
                      <CircularProgress size={16} color="inherit" />
                      <MDTypography variant="caption" color="text">
                        {messages.loadingMoreChats}
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <MDButton
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={loadMoreChats}
                    >
                      {messages.loadMoreChats}
                    </MDButton>
                  )}

                  {totalChatsCount > 0 && (
                    <MDTypography variant="caption" color="text">
                      {`${chats?.length ?? 0} / ${totalChatsCount}`}
                    </MDTypography>
                  )}
                </MDBox>
              )}

            </Suspense>
          </Suspense>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default ContactList;
